import { useEffect, useState, type RefObject } from 'react'

import type { ResolvedTheme } from '../theme/use-theme.ts'
import {
  aurora,
  bodyColours,
  formatClock,
  greeting,
  hourOf,
  moonPosition,
  skyPalette,
  starOpacity,
  sunPosition,
} from './sky-palette.ts'

export const GREETING_PLACEHOLDER = 'Olá'
export const CLOCK_PLACEHOLDER = '--:--'

/**
 * The design ticked every 5 seconds. At 60s the sun moves ~0.1% of its arc per
 * tick, which is imperceptible, and the wakeup cost drops twelvefold.
 */
const TICK_MS = 60_000

export interface SkyState {
  readonly greeting: string
  readonly clock: string
  /** Absent until a real time exists — `<time datetime="--:--">` is invalid. */
  readonly clockDateTime?: string
}

const INITIAL_STATE: SkyState = Object.freeze({
  greeting: GREETING_PLACEHOLDER,
  clock: CLOCK_PLACEHOLDER,
})

/**
 * WHY THIS IS CSSOM AND NOT REACT STATE.
 *
 * `style-src 'self'` restricts style DELIVERED IN MARKUP — parser-inserted
 * `<style>` elements and `style` attributes. It does not restrict the CSSOM:
 * assignments through `element.style.setProperty(...)` from script are explicitly
 * out of scope for the directive. So the technique the design used is available to
 * us, while a server-rendered `style="..."` is not.
 *
 * Writing these as React-rendered inline styles would be blocked outright; writing
 * them as classes would need a class per minute of the day.
 */
function paintSky(target: HTMLElement, hour: number, theme: ResolvedTheme): void {
  const [top, middle, horizon] = skyPalette(hour, theme)
  const sun = sunPosition(hour)
  const moon = moonPosition(hour)
  const glow = aurora(hour, theme)
  const colours = bodyColours(theme)

  const properties: readonly (readonly [string, string])[] = [
    ['--sky-top', top],
    ['--sky-mid', middle],
    ['--sky-low', horizon],
    ['--sky-sun-x', `${sun.x.toFixed(2)}px`],
    ['--sky-sun-y', `${sun.y.toFixed(2)}px`],
    ['--sky-sun-opacity', sun.opacity.toFixed(3)],
    ['--sky-sun-colour', colours.sun],
    ['--sky-moon-x', `${moon.x.toFixed(2)}px`],
    ['--sky-moon-y', `${moon.y.toFixed(2)}px`],
    ['--sky-moon-opacity', moon.opacity.toFixed(3)],
    ['--sky-moon-colour', colours.moon],
    ['--sky-star-opacity', starOpacity(hour).toFixed(3)],
    ['--sky-star-colour', colours.star],
    ['--sky-aurora-x', glow.x.toFixed(1)],
    ['--sky-aurora-y', glow.y.toFixed(1)],
    ['--sky-aurora-opacity', glow.opacity.toFixed(3)],
    ['--sky-aurora-colour', glow.color],
  ]

  for (const [name, value] of properties) {
    // Scoped to the sky container, never to documentElement: these properties are
    // the sky's business and keeping them in its subtree keeps the cascade legible.
    target.style.setProperty(name, value)
  }
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Paints the sky from the reader's clock, after mount.
 *
 * HONEST DEGRADATION — state these, because a future reader will otherwise assume
 * a bug:
 *
 *  1. NO JAVASCRIPT -> a fixed daytime sky from the CSS defaults, `Olá`, `--:--`.
 *     Acceptable: the sky is decorative and the clock tells the reader nothing they
 *     do not already know.
 *  2. FIRST FRAME -> the same, for one frame.
 *  3. REDUCED MOTION -> the correct sky for the moment of load, then static. Motion
 *     the reader asked not to see should not be animated on a timer either.
 *
 * The theme arrives as a prop rather than being observed: a `MutationObserver`
 * watching for your own write is a smell, and lifting it keeps the effect's
 * dependency honest.
 */
export function useSky(
  targetRef: RefObject<HTMLElement | null>,
  theme: ResolvedTheme,
): SkyState {
  const [state, setState] = useState<SkyState>(INITIAL_STATE)

  useEffect(() => {
    const target = targetRef.current

    if (target === null) {
      return undefined
    }

    const paint = (): void => {
      const hour = hourOf(new Date())

      paintSky(target, hour, theme)
      setState({
        greeting: greeting(hour),
        clock: formatClock(hour),
        clockDateTime: formatClock(hour),
      })
    }

    paint()

    if (prefersReducedMotion()) {
      return undefined
    }

    const timer = setInterval(paint, TICK_MS)

    return () => {
      clearInterval(timer)
    }
  }, [targetRef, theme])

  return state
}
